import { NoxnLogo } from "@/components/noxn/NoxnLogo";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/lib/auth";
import {
    LayoutGrid, MapPin, FolderKanban, Settings as SettingsIcon, LogOut,
    Monitor, GitBranch, ListChecks, Mail, Building2, Users, ShieldCheck,
} from "lucide-react";

const NAV_CLIENT = [
    { to: "/account", label: "Dashboard", icon: LayoutGrid, end: true },
    { to: "/portal", label: "My Campaign", icon: FolderKanban },
    { to: "/map", label: "Screen map", icon: MapPin },
    { to: "/account/settings", label: "Settings", icon: SettingsIcon },
];

// Staff / admin / super_admin share one persistent nav — "Dashboard" merges
// the old separate Overview + Dashboard concepts into a single entry point.
const NAV_STAFF = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutGrid, end: true },
    { to: "/dashboard/screens", label: "Screens", icon: Monitor },
    { to: "/dashboard/pipeline", label: "Pipeline", icon: GitBranch },
    { to: "/dashboard/tasks", label: "Tasks", icon: ListChecks },
    { to: "/dashboard/comms", label: "Comms", icon: Mail },
    { to: "/dashboard/crm", label: "CRM", icon: Building2 },
    { to: "/dashboard/staff", label: "Staff", icon: Users },
    { to: "/dashboard/admin", label: "Admin console", icon: ShieldCheck },
    { to: "/map", label: "Screen map", icon: MapPin },
    { to: "/dashboard/settings", label: "Settings", icon: SettingsIcon },
];

const navFor = (role) => (role === "client" ? NAV_CLIENT : NAV_STAFF);

/**
 * Persistent shell for the customer portal + account pages.
 * - Left nav is sticky (`lg:sticky lg:top-0`) so it stays visible while
 *   scrolling long campaign lists.
 * - Rounded edges (12–16px), refined spacing, and a distinct navy panel
 *   so the nav feels structured next to the content column.
 */
export const AccountLayout = ({ children, title = "My account", mainClassName }) => {
    const { user, logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const NAV = navFor(user?.role);

    const isActive = (item) =>
        item.end ? location.pathname === item.to : location.pathname.startsWith(item.to);

    const signOut = async () => {
        await logout();
        navigate("/");
    };

    return (
        <div className="min-h-screen bg-[#0F1E3B] text-white flex flex-col" data-testid="account-layout">
            <header className="border-b border-white/10 px-5 lg:px-8 h-16 flex items-center justify-between shrink-0 bg-[#0F1E3B]/80 backdrop-blur sticky top-0 z-30">
                <Link to="/" data-testid="account-logo" className="flex items-center">
                    <NoxnLogo variant="auto" className="h-6 w-auto" />
                </Link>
                <div className="hidden sm:block font-mono-tech text-[10px] uppercase tracking-[0.35em] text-[#FF5A00]">/ {title}</div>
                <div className="flex items-center gap-4">
                    <span className="hidden md:block text-xs text-slate-400 truncate max-w-[180px]" data-testid="account-user-email">
                        {user?.name || user?.email}
                    </span>
                    <button onClick={signOut} data-testid="account-signout-btn"
                        className="inline-flex items-center gap-2 text-xs font-mono-tech uppercase tracking-widest text-slate-400 hover:text-[#FF5A00] transition-colors">
                        <LogOut size={13} /> Sign out
                    </button>
                </div>
            </header>

            <div className="flex-1 flex flex-col lg:flex-row min-h-0">
                <aside className="lg:w-[260px] shrink-0 lg:sticky lg:top-16 lg:h-[calc(100vh-4rem)] border-b lg:border-b-0 lg:border-r border-white/10 bg-[#0F1E3B] px-3 lg:px-5 py-4 lg:py-8">
                    <div className="hidden lg:block font-mono-tech text-[10px] uppercase tracking-[0.35em] text-slate-500 mb-5 pl-3">/ Menu</div>
                    <nav className="flex lg:flex-col overflow-x-auto lg:overflow-visible gap-2" data-testid="account-nav">
                        {NAV.map((item) => {
                            const active = isActive(item);
                            return (
                                <Link key={item.to} to={item.to}
                                    data-testid={`account-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    className={`group relative inline-flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-[12px] font-medium tracking-wide whitespace-nowrap transition-all ${
                                        active
                                            ? "bg-[#FF5A00]/12 text-white shadow-[0_1px_0_0_rgba(255,90,0,0.35)_inset]"
                                            : "text-slate-400 hover:text-white hover:bg-white/5"
                                    }`}>
                                    <span className={`absolute left-0 top-2 bottom-2 w-[3px] rounded-full transition-all ${active ? "bg-[#FF5A00]" : "bg-transparent group-hover:bg-white/10"}`} />
                                    <item.icon size={15} strokeWidth={1.8} className={active ? "text-[#FF5A00]" : "text-slate-500 group-hover:text-white"} />
                                    <span>{item.label}</span>
                                </Link>
                            );
                        })}
                    </nav>
                </aside>

                <main className={`flex-1 min-w-0 ${mainClassName ?? "px-5 lg:px-10 py-8 lg:py-12"}`}>
                    <motion.div
                        key={location.pathname}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                    >
                        {children}
                    </motion.div>
                </main>
            </div>
        </div>
    );
};
